import { inject, injectable } from 'inversify'
import { nanoid } from 'nanoid/non-secure'
import { Group } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import type { AuxiliaryMouseHelper } from '@/plugins/cad/module/CADModule/auxiliaryTool/auxiliaryMouseHelper'
import {
  type MouseTipHelper,
  MouseTipId,
} from '@/plugins/cad/module/CADModule/auxiliaryTool/MouseTipHelper'
import type { CADController } from '@/plugins/cad/module/CADModule/CADController'
import { DrawEntityCommand } from '@/plugins/cad/module/CADModule/command/DrawEntityCommand'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { HistoryManager } from '@/plugins/cad/module/CADModule/manager/HistoryManager'
import type { IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import { PolylineEntity } from '@/plugins/cad/module/CADModule/model/entity'
import { createCadLine2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import type { EntitySelector } from '@/plugins/cad/module/CADModule/selector/entitySelector'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import LineDimensionGroup from '@/plugins/cad/module/objects/LineDimensionGroup'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { KeyCodes } from '@/shared/constants'

/**
 * 矩形绘制模式。
 */
export enum RectDrawMode {
  /** 对角点法：两个对角点 */
  TwoCorners = 1,
  /** 底边加高度法：底边两端点加高度点 */
  BaseHeight,
}

const rectMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
    entryFinished: () => {},
  },
  guards: {
    isMode: () => false,
  },
}).createMachine({
  id: 'drawRectTool',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: [
          { target: 'draw.baseHeight', guard: { type: 'isMode', params: RectDrawMode.BaseHeight } },
          { target: 'draw.twoCorners' },
        ],
      },
    },
    draw: {
      initial: 'twoCorners',
      states: {
        // 模式一：两点对角
        twoCorners: {
          initial: 'point1',
          states: {
            point1: {
              on: { NEXT: 'point2' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.rect.firstCorner },
            },
            point2: {
              on: { NEXT: '#drawRectTool.finished' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.rect.otherCorner },
            },
          },
        },
        // 模式二：底边+高度
        baseHeight: {
          initial: 'point1',
          states: {
            point1: {
              on: { NEXT: 'point2' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.rect.firstBasePoint },
            },
            point2: {
              on: { NEXT: 'point3' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.rect.secondBasePoint },
            },
            point3: {
              on: { NEXT: '#drawRectTool.finished', PREVIOUS: 'point2' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.rect.height },
            },
          },
        },
      },
    },
    finished: { entry: 'entryFinished', type: 'final' },
  },
})

/**
 * 矩形绘制工具，支持对角点与底边加高度两种模式。
 */
@injectable()
export class DrawRectTool extends StateMachineTool<typeof rectMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawRectTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.UnitPresentationService)
  private readonly unitPresentation!: UnitPresentationService

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = rectMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
      entryFinished: () => {
        this.updateEntity()
        this.exit()
      },
    },
    guards: {
      isMode: (_v, mode) => {
        return this.mode === mode
      },
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private mode: RectDrawMode = RectDrawMode.TwoCorners
  private points: IPoint[] = []
  private tempPoint: IPoint | null = null
  private group = new Group()
  private rectObject: Line2 | null = null
  private lengthDimensionGroup: LineDimensionGroup | null = null
  private widthDimensionGroup: LineDimensionGroup | null = null
  private entity: PolylineEntity | null = null
  private currentCommand: DrawEntityCommand | null = null
  private rectPoints: IPoint[] = []

  protected onInitialize(
    entityData?: Partial<PolylineEntity>,
    mode: RectDrawMode = RectDrawMode.TwoCorners
  ): void {
    this.mode = mode
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawEntityCommand(
      this.entity,
      this.viewManager,
      this.handleUndo.bind(this),
      this.isCanCommit.bind(this),
      this.isCanUndoStep.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.rectObject && DrawUtils.disposeObject(this.rectObject)
    this.lengthDimensionGroup?.dispose()
    this.widthDimensionGroup?.dispose()
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.points = []
    this.tempPoint = null
    this.rectObject = null
    this.lengthDimensionGroup = null
    this.widthDimensionGroup = null
    this.entity = null
    this.currentCommand = null
    this.rectPoints = []
  }

  /**
   * 鼠标按下：左键推进状态机并记录一个点。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.tempPoint) {
        this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
        this.tempPoint = null
        this.send({ type: 'NEXT' })
        this.updateCanvas()
        this.historyManager.refresh()
      }
    } else if (params.event.button === 2) {
      // 右键
    }
  }

  /**
   * 鼠标移动：更新矩形预览点。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  /** 每帧刷新长度与宽度标注 */
  onViewerUpdate() {
    this.lengthDimensionGroup?.update()
    this.widthDimensionGroup?.update()
  }

  // 正交/捕捉/极轴/点云 统一处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }

    const startPoint = this.points.length > 0 ? this.points[this.points.length - 1] : undefined
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })

    this.tempPoint = {
      x: finalPoint.x,
      y: finalPoint.y,
    }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  /************ 渲染 ************/
  private updateCanvas() {
    this.updateObject()
  }

  private updateObject() {
    const points = [...this.points]
    if (this.tempPoint) {
      points.push(this.tempPoint)
    }
    if (points.length < 2) {
      return
    }

    // 计算矩形顶点
    this.rectPoints = this.getRectPointsByMode(this.mode, points)
    const drawPoints = this.rectPoints.length === 4 ? this.rectPoints : points
    this.updateRectObject(drawPoints)

    // 绘制标注(长度、宽度)
    this.updateDimensionGroup(drawPoints)
  }

  private updateDimensionGroup(points: IPoint[]) {
    if (points.length < 2) {
      return
    }

    const [p1, p2, p3, p4] = points
    const options = {
      showAngle: false,
    }
    const getDimensionGroup = (pointA: IPoint, pointB: IPoint) => {
      return new LineDimensionGroup(
        pointA,
        pointB,
        options,
        this.group,
        this.controller.domElement!.parentElement!,
        (point: IPoint) => this.controller.getScreenPosition(point),
        (radius: number) => this.controller.getPixelSizeInWorld(radius),
        this.unitPresentation
      )
    }

    // 只有两点时，只标注一条边长
    if (points.length !== 4) {
      this.lengthDimensionGroup
        ? this.lengthDimensionGroup.updateLine(p1, p2)
        : (this.lengthDimensionGroup = getDimensionGroup(p1, p2))
      return
    }

    const line1 = { startPoint: p2, endPoint: p3 }
    const line2 = { startPoint: p3, endPoint: p4 }
    if (!this.getRectClockwise(p1, p3)) {
      // 逆时针，交换标注位置
      ;[line1.startPoint, line1.endPoint] = [line1.endPoint, line1.startPoint]
      ;[line2.startPoint, line2.endPoint] = [line2.endPoint, line2.startPoint]
    }
    this.lengthDimensionGroup
      ? this.lengthDimensionGroup.updateLine(line1.startPoint, line1.endPoint)
      : (this.lengthDimensionGroup = getDimensionGroup(line1.startPoint, line1.endPoint))
    this.widthDimensionGroup
      ? this.widthDimensionGroup.updateLine(line2.startPoint, line2.endPoint)
      : (this.widthDimensionGroup = getDimensionGroup(line2.startPoint, line2.endPoint))
  }

  private updateRectObject(points: IPoint[]) {
    if (!this.entity || points.length < 2) {
      return
    }
    const geometry = DrawUtils.getLineGeometryByPoints(points.concat(points[0]))
    if (!this.rectObject) {
      this.rectObject = createCadLine2Object(
        geometry,
        MaterialUtils.createMaterialByLineType(
          this.entity.lineType,
          MaterialUtils.getMaterialOptionByEntity(this.entity)
        )
      )
      this.rectObject.computeLineDistances()
      this.group.add(this.rectObject)
      return
    }
    this.rectObject.geometry.dispose()
    this.rectObject.geometry = geometry
    this.rectObject.computeLineDistances()
  }

  /************ 数据创建 ************/
  private createEntity(entityData?: Partial<PolylineEntity>): PolylineEntity {
    const entity = new PolylineEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
      vertices: [],
      isClosed: true,
    })
    return entity
  }

  private updateEntity() {
    if (this.entity && this.rectPoints.length === 4) {
      this.entity.vertices = this.rectPoints.map((p) => ({ x: p.x, y: p.y, bulge: 0 }))
    }
  }

  private handleUndo() {
    if (this.isCanUndoStep()) {
      this.points.pop()
      this.updateCanvas()
      this.send({ type: 'PREVIOUS' })
    }
  }

  private isCanCommit(): boolean {
    if (!this.entity || this.entity.vertices.length !== 4) {
      return false
    }
    const [p1, p2, p3, _p4] = this.entity.vertices
    const d1 = Math.hypot(p2.x - p1.x, p2.y - p1.y)
    const d2 = Math.hypot(p3.x - p2.x, p3.y - p2.y)
    const esp = 1e-6
    return d1 > esp && d2 > esp
  }

  private isCanUndoStep(): boolean {
    return this.points.length > 1
  }

  private getRectPointsByMode(mode: RectDrawMode, points: IPoint[]): IPoint[] {
    if (mode === RectDrawMode.TwoCorners && points.length >= 2) {
      const [p1, p2] = points.slice(-2)
      return this.getRectPointsByTwoCorners(p1, p2)
    } else if (mode === RectDrawMode.BaseHeight && points.length >= 3) {
      const [p1, p2, p3] = points.slice(-3)
      return this.getRectByBaseAndHeight(p1, p2, p3)
    }
    return []
  }

  private getRectPointsByTwoCorners(p1: IPoint, p2: IPoint): IPoint[] {
    // 顶点 1: 起点 P1
    const pt1 = { x: p1.x, y: p1.y }

    // 顶点 2: (P2.x, P1.y) -> 与 P1 水平
    const pt2 = { x: p2.x, y: p1.y }

    // 顶点 3: 对角点 P2
    const pt3 = { x: p2.x, y: p2.y }

    // 顶点 4: (P1.x, P2.y) -> 与 P1 垂直
    const pt4 = { x: p1.x, y: p2.y }

    // 返回顶点的顺序决定了绘制的方向（顺时针或逆时针取决于 P1 和 P2 的相对位置）
    return [pt1, pt2, pt3, pt4]
  }

  private getRectByBaseAndHeight(p1: IPoint, p2: IPoint, p3: IPoint): IPoint[] {
    // 1. 计算底边向量 (dx, dy)
    const dx = p2.x - p1.x
    const dy = p2.y - p1.y
    const baseLength = Math.sqrt(dx * dx + dy * dy)

    if (baseLength === 0) return [p1, p1, p1, p1]

    // 2. 计算底边的单位法向量 (垂直向量)
    // 垂直向量为 (-dy, dx)
    const ux = -dy / baseLength
    const uy = dx / baseLength

    // 3. 计算鼠标点到 $P_1P_2$ 直线的投影高度 (带符号)
    // 利用向量投影公式: h = (PM · n)
    const h = (p3.x - p1.x) * ux + (p3.y - p1.y) * uy

    // 4. 计算偏移向量
    const offsetX = ux * h
    const offsetY = uy * h

    // 5. 生成四个顶点 (按顺时针/逆时针顺序)
    return [
      { x: p1.x, y: p1.y }, // 顶点1: 起点
      { x: p2.x, y: p2.y }, // 顶点2: 底边终点
      { x: p2.x + offsetX, y: p2.y + offsetY }, // 顶点3: 垂直偏移后的终点
      { x: p1.x + offsetX, y: p1.y + offsetY }, // 顶点4: 垂直偏移后的起点
    ]
  }

  // 根据两个对角点判断矩形的绘制方向（顺时针或逆时针）
  private getRectClockwise(p1: IPoint, p2: IPoint) {
    const crossProduct = (p2.x - p1.x) * (p2.y - p1.y)
    return !(crossProduct > 0)
  }
}
